import { memo, useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  getCitiesByCountryName,
  Grid,
  maxLengthValidator,
  ReactHookFormAutocomplete,
  useTranslations,
  validateAddress,
  validateZIPCode,
  FormHiddenInput,
  validateEmail,
  validateVAT,
  validatePhone,
  emptyStringValidator,
  ReactHookFormTextField,
} from '@uniqkey-frontend/shared-app';
import { useForm } from 'react-hook-form';
import {
  UpdatePartnerOrganizationRequest, GetOrganizationByIdResponse,
} from '@uniqkey-backend-partner/api-client';
import { COUNTRIES_LIST } from '../../../../../../helpers/countries';

interface IEditOrganizationDetailsModalFormValue extends Omit<
  UpdatePartnerOrganizationRequest, 'country' | 'city' | 'externalId'
> {
  country: UpdatePartnerOrganizationRequest['country'] | null;
  city: UpdatePartnerOrganizationRequest['city'] | null;
  externalId: string;
}

export interface IEditOrganizationDetailsModalReturnValue
  extends UpdatePartnerOrganizationRequest {}

interface IEditOrganizationDetailsModalProps {
  isOpen: boolean;
  isLoading: boolean;
  onSubmit: (params: IEditOrganizationDetailsModalReturnValue) => Promise<void> | void;
  onClose: () => void;
  organization: GetOrganizationByIdResponse;
}

const EditOrganizationDetailsModal = (props: IEditOrganizationDetailsModalProps) => {
  const {
    isOpen, isLoading, onSubmit, onClose, organization,
  } = props;

  const { t } = useTranslations();

  const {
    name, country, billingEmail, city, vat, address, phone, zip, externalId,
  } = organization;

  const {
    handleSubmit,
    formState: { isDirty, errors },
    control,
    setValue,
    watch,
  } = useForm<IEditOrganizationDetailsModalFormValue>({
    defaultValues: {
      name,
      country,
      billingEmail,
      city,
      vat,
      address,
      phone,
      zip,
      externalId: externalId ?? '',
    },
    mode: 'all',
  });

  const [
    selectedCountry,
  ] = watch([
    'country',
  ]);

  const citiesOptions = useMemo(() => (selectedCountry
    ? getCitiesByCountryName(selectedCountry)
    : []
  ), [selectedCountry]);

  const handleCountryChange = useCallback(() => {
    setValue('city', null);
  }, [setValue]);

  const handleFormSubmit = useCallback((formValue: IEditOrganizationDetailsModalFormValue) => {
    const { externalId: preExternalId, ...rest } = formValue;
    const processedExternalId = preExternalId ? preExternalId.trim() : null;
    onSubmit({
      ...rest,
      externalId: processedExternalId || null,
    } as IEditOrganizationDetailsModalReturnValue);
  }, [onSubmit]);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      clickOutsideToClose={!isLoading}
    >
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        autoComplete="off"
        noValidate
      >
        <DialogTitle isLoading={isLoading} onClose={onClose}>
          {t('editOrganizationDetailsModal.title')}
        </DialogTitle>
        <DialogContent>
          <Grid container direction="row" rowSpacing={4} columnSpacing={2}>
            <Grid item xs={6}>
              <FormHiddenInput />
              <ReactHookFormTextField
                name="name"
                control={control}
                fullWidth
                autoFocus
                autoComplete="none"
                label={`${t('editOrganizationDetailsModal.organizationName.label')}*`}
                placeholder={t('editOrganizationDetailsModal.organizationName.placeholder')}
                error={!!errors.name}
                helperText={errors.name?.message}
                rules={{
                  required: t('validation.required'),
                  validate: (value) => (
                    emptyStringValidator(value as string) ? t('validation.required') : true
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormAutocomplete
                t={t}
                name="country"
                onChange={handleCountryChange}
                options={COUNTRIES_LIST}
                control={control}
                label={`${t('editOrganizationDetailsModal.country.label')}*`}
                placeholder={t('editOrganizationDetailsModal.country.placeholder')}
                error={!!errors.country}
                helperText={errors.country?.message}
                disableAutoFill
                rules={{ required: t('validation.required') }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormTextField
                name="billingEmail"
                control={control}
                fullWidth
                autoComplete="none"
                label={`${t('editOrganizationDetailsModal.billingEmail.label')}*`}
                placeholder={t('editOrganizationDetailsModal.billingEmail.placeholder')}
                error={!!errors.billingEmail}
                helperText={errors.billingEmail?.message}
                rules={{
                  required: t('validation.required'),
                  validate: {
                    isValid: (v) => validateEmail(v as string) || t('validation.invalidEmail'),
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormAutocomplete
                t={t}
                name="city"
                freeSolo
                options={citiesOptions}
                control={control}
                label={`${t('editOrganizationDetailsModal.city.label')}*`}
                placeholder={t('editOrganizationDetailsModal.city.placeholder')}
                error={!!errors.city}
                helperText={errors.city?.message}
                disableAutoFill
                disabled={!selectedCountry}
                rules={{
                  validate: {
                    isValid: (v) => validateAddress(v as string) || t('validation.invalidCity'),
                    required: (v) => (selectedCountry && !v ? t('validation.required') : true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormTextField
                name="vat"
                control={control}
                fullWidth
                autoComplete="none"
                label={`${t('editOrganizationDetailsModal.vat.label')}*`}
                placeholder={t('editOrganizationDetailsModal.vat.placeholder')}
                error={!!errors.vat}
                helperText={errors.vat?.message}
                rules={{
                  required: t('validation.required'),
                  validate: {
                    isValid: (v) => validateVAT(v as string)
                        || t('validation.invalidVAT'),
                    maxLength: (v) => maxLengthValidator(20, v as string)
                        || t('validation.maxLength', { length: 20 }),
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormTextField
                name="address"
                control={control}
                fullWidth
                autoComplete="none"
                label={`${t('editOrganizationDetailsModal.address.label')}*`}
                placeholder={t('editOrganizationDetailsModal.address.placeholder')}
                error={!!errors.address}
                helperText={errors.address?.message}
                rules={{
                  required: t('validation.required'),
                  validate: {
                    isValid: (v) => validateAddress(v as string)
                        || t('validation.invalidAddress'),
                    required: (value) => (emptyStringValidator(value as string)
                      ? t('validation.required')
                      : true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormTextField
                name="phone"
                control={control}
                fullWidth
                autoComplete="none"
                label={`${t('editOrganizationDetailsModal.phone.label')}*`}
                placeholder={t('editOrganizationDetailsModal.phone.placeholder')}
                error={!!errors.phone}
                helperText={errors.phone?.message}
                rules={{
                  required: t('validation.required'),
                  validate: {
                    isValid: (v) => validatePhone(v as string)
                        || t('validation.invalidPhone'),
                    maxLength: (v) => maxLengthValidator(19, v as string)
                        || t('validation.maxLength', { length: 19 }),
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormTextField
                name="zip"
                control={control}
                fullWidth
                autoComplete="none"
                label={`${t('editOrganizationDetailsModal.zipCode.label')}*`}
                placeholder={t('editOrganizationDetailsModal.zipCode.placeholder')}
                error={!!errors.zip}
                helperText={errors.zip?.message}
                rules={{
                  required: t('validation.required'),
                  validate: {
                    isValid: (v) => validateZIPCode(v as string)
                        || t('validation.invalidZIPCode'),
                    maxLength: (v) => maxLengthValidator(12, v as string)
                        || t('validation.maxLength', { length: 12 }),
                    required: (value) => (emptyStringValidator(value as string)
                      ? t('validation.required')
                      : true),
                  },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <ReactHookFormTextField
                name="externalId"
                control={control}
                fullWidth
                autoComplete="none"
                label={t('editOrganizationDetailsModal.externalId.label')}
                placeholder={t('editOrganizationDetailsModal.externalId.placeholder')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container columnSpacing={2} justifyContent="flex-end">
            <Grid item xs={2.5}>
              <Button fullWidth isLoading={isLoading} disabled={!isDirty} type="submit">
                {t('common.save')}
              </Button>
            </Grid>
            <Grid item xs={2.5}>
              <Button fullWidth variant="outlined" disabled={isLoading} onClick={onClose}>
                {t('common.cancel')}
              </Button>
            </Grid>
          </Grid>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default memo(EditOrganizationDetailsModal);
